import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { Check, Clock, ArrowLeft, CalendarDays, User, Users, Scissors, Plus, X, ShoppingBag, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ['services', 'staff', 'datetime', 'details', 'confirmed'];

export default function PublicBookingPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [cart, setCart] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [holdId, setHoldId] = useState(null);
  const [openCats, setOpenCats] = useState(new Set());
  const [cancellingBooking, setCancellingBooking] = useState(false);
  const [cancelledBooking, setCancelledBooking] = useState(false);
  const [clientUser, setClientUser] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [signInForm, setSignInForm] = useState({ email: '', password: '' });
  const [signingIn, setSigningIn] = useState(false);
  // Group bookings: extra guests booked alongside the primary customer. Each guest is
  // { key, name } and each cart item has assignedTo: 'me' | guest.key.
  const [guests, setGuests] = useState([]);
  const [guestNameDraft, setGuestNameDraft] = useState('');

  const fetchBiz = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/book/${slug}`);
      setBiz(data);
      const params = new URLSearchParams(window.location.search);
      const locId = params.get('location');
      const locs = data.locations || [];
      if (locId && locs.find(l => l.id === locId)) {
        setSelectedLocation(locs.find(l => l.id === locId));
      } else if (locs.length <= 1) {
        setSelectedLocation(locs[0] || { id: '', name: data.business_name });
      }
    } catch {
      setError('Business not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchBiz(); }, [fetchBiz]);

  // Detect existing client login on mount
  useEffect(() => {
    axios.get(`${API}/client-auth/me`, { withCredentials: true })
      .then(({ data }) => {
        setClientUser(data);
        setForm(p => ({
          ...p,
          name: p.name || data.name || '',
          email: p.email || data.email || '',
          phone: p.phone || data.phone || '',
        }));
      })
      .catch(() => {});
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!signInForm.email || !signInForm.password) { toast.error('Email and password required'); return; }
    setSigningIn(true);
    try {
      const { data } = await axios.post(`${API}/client-auth/login`, signInForm, { withCredentials: true });
      setClientUser(data);
      setForm(p => ({
        ...p,
        name: data.name || p.name,
        email: data.email || p.email,
      }));
      // Fetch full profile to also prefill phone
      try {
        const { data: me } = await axios.get(`${API}/client-auth/me`, { withCredentials: true });
        setForm(p => ({ ...p, phone: p.phone || me.phone || '' }));
      } catch {}
      setShowSignIn(false);
      setSignInForm({ email: '', password: '' });
      toast.success(`Welcome back, ${data.name || data.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign in failed');
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try { await axios.post(`${API}/client-auth/logout`, {}, { withCredentials: true }); } catch {}
    setClientUser(null);
    setForm({ name: '', email: '', phone: '', notes: '' });
    toast.success('Signed out');
  };

  const fetchAvailability = useCallback(async () => {
    if (!selectedStaff || !selectedDate) return;
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const cartDur = cart.reduce((s, c) => s + (c.duration || 0), 0) || 30;
      const staffParam = selectedStaff.id === 'any' ? 'any' : selectedStaff.id;
      const svcIds = cart.map(s => s.id).join(',');
      // Build parallel `chains` (one per person). If any guest has services assigned, we send
      // this to the availability endpoint so it verifies distinct staff can serve each chain
      // in parallel — a plain service list would let a single free staff falsely satisfy the
      // whole group by running everyone sequentially.
      const chainsByPerson = cart.reduce((acc, c) => {
        const k = c.assignedTo || 'me';
        (acc[k] = acc[k] || []).push(c.id);
        return acc;
      }, {});
      const chainKeys = Object.keys(chainsByPerson);
      const chainsParam = chainKeys.length >= 2
        ? chainKeys.map(k => chainsByPerson[k].join(',')).join('|')
        : '';
      const qs = `staff_id=${staffParam}&date=${dateStr}&duration=${cartDur}&service_ids=${svcIds}${chainsParam ? `&chains=${encodeURIComponent(chainsParam)}` : ''}`;
      const { data } = await axios.get(`${API}/public/book/${slug}/availability?${qs}`);
      setAvailableSlots(data.available_slots || []);
    } catch {
      setAvailableSlots([]);
    }
  }, [slug, selectedStaff, selectedDate, cart]);

  useEffect(() => { if (step === 2) fetchAvailability(); }, [step, fetchAvailability]);

  // Release hold on page unload / tab close
  useEffect(() => {
    const releaseHold = () => {
      if (holdId) {
        navigator.sendBeacon(`${API}/public/book/${slug}/hold/${holdId}`, '');
      }
    };
    window.addEventListener('beforeunload', releaseHold);
    return () => window.removeEventListener('beforeunload', releaseHold);
  }, [holdId, slug]);


  const addToCart = (svc) => {
    setCart(prev => [...prev, { ...svc, assignedTo: 'me' }]);
    toast.success(`Added ${svc.name}`);
  };

  const removeFromCart = (idx) => {
    setCart(prev => prev.filter((_, i) => i !== idx));
  };

  const setCartItemAssignee = (idx, assignedTo) => {
    setCart(prev => prev.map((c, i) => i === idx ? { ...c, assignedTo } : c));
  };

  const addGuest = () => {
    const name = (guestNameDraft || '').trim();
    if (!name) { toast.error('Please enter the guest\'s name'); return; }
    const key = `g${Date.now()}`;
    setGuests(prev => [...prev, { key, name }]);
    setGuestNameDraft('');
    toast.success(`${name} added — assign services to them below`);
  };

  const removeGuest = (key) => {
    setGuests(prev => prev.filter(g => g.key !== key));
    // Any services assigned to this guest fall back to the primary customer.
    setCart(prev => prev.map(c => c.assignedTo === key ? { ...c, assignedTo: 'me' } : c));
  };

  const cartTotal = cart.reduce((s, c) => s + (c.price || 0), 0);
  // Duration used for availability query: max across all people (they run in parallel),
  // not the naive sum. Each person's chain runs sequentially in their own timeline.
  const durationByPerson = cart.reduce((acc, c) => {
    const key = c.assignedTo || 'me';
    acc[key] = (acc[key] || 0) + (c.duration || 0);
    return acc;
  }, {});
  const cartDuration = Object.values(durationByPerson).reduce((m, d) => Math.max(m, d), 0) || 30;

  const handleBook = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name is required'); return; }
    if (!form.email) { toast.error('Email is required'); return; }
    if (!form.phone) { toast.error(t('publicBooking.phone') + ' required'); return; }
    setBooking(true);
    try {
      const primaryServices = cart.filter(c => (c.assignedTo || 'me') === 'me').map(s => s.id);
      const guestsPayload = guests
        .map(g => ({ name: g.name, service_ids: cart.filter(c => c.assignedTo === g.key).map(s => s.id) }))
        .filter(g => g.service_ids.length > 0);
      if (primaryServices.length === 0 && guestsPayload.length === 0) {
        toast.error('Please select at least one service');
        setBooking(false);
        return;
      }
      const { data } = await axios.post(`${API}/public/book/${slug}`, {
        client_name: form.name, client_email: form.email, client_phone: form.phone,
        services: primaryServices,
        guests: guestsPayload,
        staff_id: selectedStaff.id === 'any' ? (selectedStaff.resolvedId || '') : selectedStaff.id,
        client_requested: selectedStaff.id !== 'any',
        date: format(selectedDate, 'yyyy-MM-dd'), start_time: selectedTime, notes: form.notes,
        location_id: selectedLocation?.id || '', hold_id: holdId || ''
      }, { withCredentials: true });
      setConfirmed(data);
      setStep(4);
      setHoldId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setBooking(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]"><div className="w-10 h-10 border-3 border-[#556B2F] border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]"><p className="text-[#E2725B] text-lg">{error}</p></div>;

  // Group services by category
  const grouped = (biz?.services || []).reduce((acc, s) => {
    const cat = s.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});
  const sortedCats = Object.keys(grouped).sort((a, b) => a === 'General' ? 1 : b === 'General' ? -1 : a.localeCompare(b));

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="public-booking-page">
      <div className="bg-[#556B2F] text-white py-6">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>{biz?.business_name}</h1>
          <p className="text-white/70 text-sm mt-1">Book your appointment online</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        <div className="flex items-center justify-center gap-1">
          {['Services', 'Staff', 'Date & Time', 'Your Details', 'Confirmed'].map((label, i) => (
            <div key={label} className="flex items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= step ? 'bg-[#556B2F] text-white' : 'bg-[#E5E7E1] text-[#5C6656]'}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className={`text-xs ml-1 mr-3 hidden sm:inline ${i <= step ? 'text-[#556B2F] font-medium' : 'text-[#5C6656]'}`}>{label}</span>
              {i < 4 && <div className={`w-6 h-0.5 ${i < step ? 'bg-[#556B2F]' : 'bg-[#E5E7E1]'}`} />}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-12">
        {/* Location picker (only for multi-location, before services) */}
        {!selectedLocation && (biz?.locations || []).length > 1 && (
          <div className="fade-in space-y-3" data-testid="step-location">
            <h2 className="text-xl font-medium text-[#1A2421] mb-2" style={{ fontFamily: 'Outfit' }}>{t('publicBooking.selectService')}</h2>
            <p className="text-sm text-[#5C6656] mb-4">Select the location you'd like to visit</p>
            <div className="space-y-2">
              {(biz?.locations || []).map(loc => (
                <button key={loc.id} onClick={() => setSelectedLocation(loc)}
                  className="w-full text-left p-4 rounded-xl border border-[#E5E7E1] hover:border-[#556B2F] transition-colors bg-white" data-testid={`loc-pick-${loc.id}`}>
                  <p className="font-semibold text-[#1A2421]">{loc.name}</p>
                  {loc.address && <p className="text-sm text-[#5C6656] mt-1">{[loc.address, loc.city, loc.postcode].filter(Boolean).join(', ')}</p>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Show booking steps only after location is selected (or auto-assigned) */}
        {selectedLocation && (<>
        {/* Step 1: Select Services (multi) */}
        {step === 0 && (
          <div className="fade-in space-y-3" data-testid="step-service">
            <h2 className="text-xl font-medium text-[#1A2421] mb-2" style={{ fontFamily: 'Outfit' }}>{t('publicBooking.selectService')}</h2>
            <p className="text-sm text-[#5C6656] mb-4">Select one or more treatments for your visit</p>

            {/* Cart summary */}
            {cart.length > 0 && (
              <div className="sticky top-0 z-10 bg-[#556B2F] text-white rounded-xl p-4 mb-4 shadow-lg" data-testid="service-cart">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <ShoppingBag size={16} />
                    <span className="font-medium text-sm">{cart.length} treatment{cart.length > 1 ? 's' : ''} selected</span>
                  </div>
                  <span className="font-bold">{'\u00A3'}{cartTotal.toFixed(2)} · {cartDuration}min</span>
                </div>
                <div className="space-y-2 mb-3">
                  {cart.map((s, i) => {
                    const assignedTo = s.assignedTo || 'me';
                    return (
                      <div key={i} className="flex items-center justify-between gap-2 text-sm text-white/90 bg-white/10 rounded px-2 py-1.5" data-testid={`cart-item-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{s.name}</div>
                          {guests.length > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-[10px] uppercase text-white/60 mr-1">For</span>
                              <select
                                value={assignedTo}
                                onChange={(e) => setCartItemAssignee(i, e.target.value)}
                                className="text-xs bg-white/20 text-white rounded px-1 py-0.5 outline-none border border-white/10"
                                data-testid={`cart-item-${i}-assignee`}
                              >
                                <option value="me" className="text-[#1A2421]">You</option>
                                {guests.map(g => (
                                  <option key={g.key} value={g.key} className="text-[#1A2421]">{g.name}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                        <button type="button" onClick={() => removeFromCart(i)} className="hover:text-white p-0.5 flex-shrink-0" data-testid={`cart-remove-${i}`}><X size={14} /></button>
                      </div>
                    );
                  })}
                </div>

                {/* Guests section — add another person to the booking */}
                <div className="mb-3 pt-2 border-t border-white/20">
                  {guests.length > 0 && (
                    <div className="space-y-1 mb-2">
                      {guests.map(g => {
                        const gServices = cart.filter(c => c.assignedTo === g.key);
                        return (
                          <div key={g.key} className="flex items-center justify-between text-xs text-white/80 bg-white/10 rounded px-2 py-1" data-testid={`guest-row-${g.key}`}>
                            <span>{g.name} · {gServices.length} service{gServices.length !== 1 ? 's' : ''}</span>
                            <button type="button" onClick={() => removeGuest(g.key)} className="hover:text-white p-0.5" data-testid={`guest-remove-${g.key}`}><X size={12} /></button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={guestNameDraft}
                      onChange={(e) => setGuestNameDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }}
                      placeholder="Add friend or family (name)"
                      className="flex-1 text-xs bg-white/10 text-white placeholder-white/50 rounded px-2 py-1.5 outline-none border border-white/20"
                      data-testid="guest-name-input"
                    />
                    <button
                      type="button"
                      onClick={addGuest}
                      className="text-xs bg-white/20 hover:bg-white/30 text-white rounded px-2 py-1.5 font-medium"
                      data-testid="add-guest-btn"
                    >
                      + Add
                    </button>
                  </div>
                </div>

                <Button onClick={() => setStep(1)} className="w-full bg-white text-[#556B2F] hover:bg-white/90 font-semibold" data-testid="continue-to-staff">
                  Continue
                </Button>
              </div>
            )}

            {sortedCats.map(cat => {
              const isOpen = openCats.has(cat);
              const catServices = grouped[cat];
              const catInCart = catServices.reduce((acc, cs) => acc + cart.filter(c => c.id === cs.id).length, 0);
              return (
                <div key={cat} className="border border-[#E5E7E1] rounded-xl bg-white overflow-hidden">
                  <button type="button" onClick={() => setOpenCats(prev => { const n = new Set(prev); if (n.has(cat)) n.delete(cat); else n.add(cat); return n; })}
                    className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-[#F9F8F6] transition-colors" data-testid={`cat-toggle-${cat}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#1A2421]">{cat}</span>
                      <span className="text-xs text-[#5C6656]">({catServices.length})</span>
                      {catInCart > 0 && <span className="text-[10px] font-bold bg-[#556B2F] text-white px-1.5 py-0.5 rounded-full">{catInCart}</span>}
                    </div>
                    <ChevronDown size={16} className={`text-[#5C6656] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="border-t border-[#E5E7E1] divide-y divide-[#E5E7E1]">
                      {catServices.map(s => {
                        const qty = cart.filter(c => c.id === s.id).length;
                        return (
                          <div key={s.id} className={`flex items-center justify-between px-4 py-3 transition-colors ${qty > 0 ? 'bg-[#F5F7F0]' : 'hover:bg-[#FAFBF7]'}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-[#1A2421] text-sm">{s.name}</p>
                              {s.description && <p className="text-xs text-[#5C6656] mt-0.5 line-clamp-1">{s.description}</p>}
                              <span className="text-xs text-[#5C6656] flex items-center gap-1 mt-0.5"><Clock size={11} />{s.duration}min</span>
                            </div>
                            <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                              <span className="text-base font-bold text-[#556B2F]">{'\u00A3'}{s.price?.toFixed(2)}</span>
                              {qty > 0 ? (
                                <div className="flex items-center gap-1" data-testid={`qty-controls-${s.id}`}>
                                  <button type="button" onClick={() => { const idx = cart.map(c => c.id).lastIndexOf(s.id); if (idx >= 0) removeFromCart(idx); }} className="w-7 h-7 rounded-full border-2 border-[#556B2F] text-[#556B2F] flex items-center justify-center hover:bg-[#556B2F] hover:text-white transition-colors font-bold" data-testid={`decrement-svc-${s.id}`}>−</button>
                                  <span className="w-7 h-7 rounded-full bg-[#556B2F] text-white flex items-center justify-center text-xs font-bold" data-testid={`qty-badge-${s.id}`}>{qty}</span>
                                  <button type="button" onClick={() => addToCart(s)} className="w-7 h-7 rounded-full border-2 border-[#556B2F] text-[#556B2F] flex items-center justify-center hover:bg-[#556B2F] hover:text-white transition-colors font-bold" data-testid={`increment-svc-${s.id}`}>+</button>
                                </div>
                              ) : (
                                <button type="button" onClick={() => addToCart(s)} className="w-8 h-8 rounded-full border-2 border-[#556B2F] text-[#556B2F] flex items-center justify-center hover:bg-[#556B2F] hover:text-white transition-colors" data-testid={`add-svc-${s.id}`}>
                                  <Plus size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Step 2: Select Staff */}
        {step === 1 && (
          <div className="fade-in space-y-3" data-testid="step-staff">
            <div className="flex items-center gap-2 mb-4">
              <Button variant="ghost" size="sm" onClick={() => setStep(0)} className="text-[#5C6656]"><ArrowLeft size={16} /></Button>
              <h2 className="text-xl font-medium text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('publicBooking.selectStaff')}</h2>
            </div>
            <div className="p-3 bg-[#E9EDDF] rounded-lg mb-4">
              <p className="text-sm text-[#556B2F] font-medium">{cart.length} treatment{cart.length > 1 ? 's' : ''} · {cartDuration}min · {'\u00A3'}{cartTotal.toFixed(2)}</p>
              <p className="text-xs text-[#556B2F]/70 mt-0.5">{cart.map(s => s.name).join(', ')}</p>
            </div>
            {/* No preference option */}
            <button data-testid="staff-no-preference" onClick={() => { setSelectedStaff({ id: 'any', name: 'Any professional' }); setStep(2); }}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-[#E5E7E1] bg-white hover:border-[#556B2F] transition-colors mb-2 text-left">
              <div className="w-12 h-12 rounded-full bg-[#F5F7F0] flex items-center justify-center">
                <Users size={20} className="text-[#556B2F]" />
              </div>
              <div>
                <p className="font-medium text-[#1A2421]">No preference</p>
                <p className="text-xs text-[#5C6656]">for maximum availability</p>
              </div>
            </button>
            {(biz?.staff || []).filter(s => {
              // Filter staff who can do ALL selected services
              const assigned = s.assigned_services || [];
              if (assigned.length === 0) return false; // No services assigned = can't be booked
              return cart.every(c => assigned.includes(c.id));
            }).filter(s => {
              // Filter staff by selected location
              if (!selectedLocation?.id) return true;
              const staffLocs = s.location_ids || [];
              if (staffLocs.length === 0) return true; // No restrictions
              return staffLocs.includes(selectedLocation.id);
            }).map(s => (
              <button key={s.id} data-testid={`staff-${s.id}`} onClick={() => { setSelectedStaff(s); setStep(2); }}
                className="w-full flex items-center gap-4 p-4 rounded-xl border border-[#E5E7E1] bg-white hover:border-[#556B2F] transition-colors mb-2 text-left">
                <div className="w-12 h-12 rounded-full bg-[#E9EDDF] flex items-center justify-center text-lg font-bold text-[#556B2F]">
                  {s.name?.charAt(0)?.toUpperCase()}
                </div>
                <p className="font-medium text-[#1A2421]">{s.name}</p>
              </button>
            ))}
          </div>
        )}

        {/* Step 3: Date & Time */}
        {step === 2 && (
          <div className="fade-in" data-testid="step-datetime">
            <div className="flex items-center gap-2 mb-4">
              <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="text-[#5C6656]"><ArrowLeft size={16} /></Button>
              <h2 className="text-xl font-medium text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('publicBooking.selectDateTime')}</h2>
            </div>
            <div className="p-3 bg-[#E9EDDF] rounded-lg mb-4">
              <p className="text-sm text-[#556B2F] font-medium">{cart.map(s => s.name).join(' + ')} with {selectedStaff?.id === 'any' ? 'any professional' : selectedStaff?.name}</p>
              <p className="text-xs text-[#556B2F]/70 mt-0.5">{cartDuration}min total · {'\u00A3'}{cartTotal.toFixed(2)}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-[#E5E7E1]">
                <CardContent className="p-3">
                  <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} className="rounded-lg" />
                </CardContent>
              </Card>
              <Card className="border-[#E5E7E1]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-[#1A2421]">{format(selectedDate, 'EEEE, MMM d')}</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[300px] overflow-y-auto">
                  {availableSlots.length === 0 ? (
                    <p className="text-center py-8 text-sm text-[#5C6656]">No available slots for this date</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map(t => (
                        <button key={t} data-testid={`slot-${t}`} onClick={async () => {
                          setSelectedTime(t);
                          // Create hold to block calendar
                          try {
                            // For group bookings send parallel `chains` so backend creates one
                            // hold row per person → both staff columns are visibly blocked.
                            // Order matters: primary ('me') always first, guests in their entry order.
                            const orderedKeys = ['me', ...guests.map(g => g.key)];
                            const chainArrays = orderedKeys
                              .map(k => cart.filter(c => (c.assignedTo || 'me') === k).map(c => c.id))
                              .filter(arr => arr.length > 0);
                            const personLabels = orderedKeys
                              .map(k => {
                                const arr = cart.filter(c => (c.assignedTo || 'me') === k);
                                if (arr.length === 0) return null;
                                if (k === 'me') return '';  // primary name unknown at hold time
                                const g = guests.find(gg => gg.key === k);
                                return g ? `Guest: ${g.name}` : '';
                              })
                              .filter(l => l !== null);
                            const { data: holdData } = await axios.post(`${API}/public/book/${slug}/hold`, {
                              staff_id: selectedStaff?.id || 'any',
                              date: format(selectedDate, 'yyyy-MM-dd'),
                              start_time: t,
                              duration: cartDuration || 30,
                              service_names: cart.map(s => s.name).join(', '),
                              chains: chainArrays.length >= 2 ? chainArrays : undefined,
                              person_labels: chainArrays.length >= 2 ? personLabels : undefined,
                            });
                            // Prefer the group id so the booking + release call cleans up ALL
                            // parallel-chain hold rows in one shot.
                            setHoldId(holdData.hold_group_id || holdData.hold_id);
                            if (holdData.staff_id && selectedStaff?.id === 'any') {
                              setSelectedStaff(prev => ({ ...prev, resolvedId: holdData.staff_id, resolvedName: holdData.staff_name }));
                            }
                          } catch { /* hold failed silently, booking can still proceed */ }
                          setStep(3);
                        }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${selectedTime === t ? 'bg-[#556B2F] text-white border-[#556B2F]' : 'border-[#E5E7E1] text-[#1A2421] hover:border-[#556B2F]'}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 4: Details */}
        {step === 3 && (
          <div className="fade-in" data-testid="step-details">
            <div className="flex items-center gap-2 mb-4">
              <Button variant="ghost" size="sm" onClick={() => {
                // Release hold when going back
                if (holdId) {
                  axios.delete(`${API}/public/book/${slug}/hold/${holdId}`).catch(() => {});
                  setHoldId(null);
                }
                setStep(2);
              }} className="text-[#5C6656]"><ArrowLeft size={16} /></Button>
              <h2 className="text-xl font-medium text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Your Details</h2>
            </div>
            <Card className="border-[#E5E7E1] mb-4">
              <CardContent className="p-4 space-y-2">
                {cart.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Scissors size={12} className="text-[#556B2F]" />
                      <span className="text-[#1A2421]">{s.name}</span>
                      <span className="text-[#5C6656] text-xs">{s.duration}min</span>
                    </div>
                    <span className="font-medium text-[#556B2F]">{'\u00A3'}{s.price?.toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-[#E5E7E1] pt-2 mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-[#5C6656]">
                    <span className="flex items-center gap-1"><User size={12} />{selectedStaff?.id === 'any' ? 'Any professional' : selectedStaff?.name}</span>
                    <span className="flex items-center gap-1"><CalendarDays size={12} />{format(selectedDate, 'MMM d')}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{selectedTime}</span>
                  </div>
                  <span className="font-bold text-[#556B2F] text-lg">{'\u00A3'}{cartTotal.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
            <form onSubmit={handleBook} className="space-y-4">
              {clientUser ? (
                <div data-testid="signed-in-banner" className="flex items-center justify-between bg-[#E9EDDF] border border-[#D4DCC0] rounded-lg px-4 py-2.5 text-sm">
                  <span className="text-[#1A2421]">Signed in as <span className="font-semibold">{clientUser.name || clientUser.email}</span></span>
                  <button type="button" onClick={handleSignOut} data-testid="sign-out-btn" className="text-[#556B2F] hover:underline font-medium">Sign out</button>
                </div>
              ) : (
                <div className="border border-[#E5E7E1] rounded-lg px-4 py-2.5 text-sm bg-white">
                  {!showSignIn ? (
                    <div className="flex items-center justify-between">
                      <span className="text-[#5C6656]">Already a customer?</span>
                      <button type="button" onClick={() => setShowSignIn(true)} data-testid="show-sign-in-btn" className="text-[#556B2F] hover:underline font-medium">Sign in to autofill</button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1A2421]">Sign in to your account</span>
                        <button type="button" onClick={() => setShowSignIn(false)} className="text-[#5C6656] hover:text-[#1A2421]"><X size={14} /></button>
                      </div>
                      <Input data-testid="sign-in-email" type="email" placeholder="Email" value={signInForm.email} onChange={e => setSignInForm(p => ({...p, email: e.target.value}))} className="border-[#E5E7E1] h-9" />
                      <Input data-testid="sign-in-password" type="password" placeholder="Password" value={signInForm.password} onChange={e => setSignInForm(p => ({...p, password: e.target.value}))} className="border-[#E5E7E1] h-9" />
                      <Button type="button" data-testid="sign-in-submit-btn" onClick={handleSignIn} disabled={signingIn} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white h-9 text-sm">
                        {signingIn ? 'Signing in...' : 'Sign in'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input data-testid="booking-name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} required className="border-[#E5E7E1]" placeholder="Your full name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input data-testid="booking-email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} className="border-[#E5E7E1]" required placeholder="your@email.com" />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input data-testid="booking-phone" value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} className="border-[#E5E7E1]" required placeholder="+44 7..." />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input data-testid="booking-notes" value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="border-[#E5E7E1]" placeholder="Any special requests?" />
              </div>
              <Button data-testid="confirm-booking-btn" type="submit" disabled={booking} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white py-5 text-base">
                {booking ? 'Booking...' : `Confirm ${cart.length} Treatment${cart.length > 1 ? 's' : ''} · ${'\u00A3'}${cartTotal.toFixed(2)}`}
              </Button>
            </form>
          </div>
        )}

        {/* Step 5: Confirmed */}
        {step === 4 && confirmed && (() => {
          const dateObj = new Date(confirmed.date + 'T12:00:00');
          const dayName = format(dateObj, 'EEEE');
          const dateDisplay = format(dateObj, 'd MMM');
          const [startTime, endTime] = (confirmed.time || '').split('-');
          const bizAddress = [biz?.address_line1, biz?.city, biz?.postcode].filter(Boolean).join(', ');
          const calTitle = `${(confirmed.services || []).map(s => s.service).join(' + ')} at ${biz?.name || ''}`;
          const calStart = `${confirmed.date}T${startTime || '09:00'}:00`;
          const calEndTime = endTime || startTime || '10:00';
          const calEnd = `${confirmed.date}T${calEndTime}:00`;
          const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calTitle)}&dates=${calStart.replace(/[-:]/g,'')}/${calEnd.replace(/[-:]/g,'')}&location=${encodeURIComponent(bizAddress)}&details=${encodeURIComponent('Booked via ' + (biz?.name || 'Apptio'))}`;
          const icsContent = `BEGIN:VCALENDAR\nVERSION:2.0\nBEGIN:VEVENT\nSUMMARY:${calTitle}\nDTSTART:${calStart.replace(/[-:]/g,'')}\nDTEND:${calEnd.replace(/[-:]/g,'')}\nLOCATION:${bizAddress}\nDESCRIPTION:Booked via ${biz?.name || 'Apptio'}\nEND:VEVENT\nEND:VCALENDAR`;

          return (
          <div className="fade-in py-8 max-w-md mx-auto" data-testid="step-confirmed">
            <h2 className="text-2xl font-bold text-[#1A2421] mb-1" style={{ fontFamily: 'Outfit' }}>Thanks for booking</h2>
            <p className="text-[#5C6656] mb-6">Your appointment is coming up.</p>

            {clientUser && (
              <a href="/my-bookings" data-testid="manage-bookings-link" className="block mb-5 text-center bg-[#1A2421] hover:bg-black text-white rounded-lg py-3 font-medium text-sm">
                Manage all your bookings
              </a>
            )}

            {/* Appointment card */}
            <div className="border border-[#E5E7E1] rounded-xl p-5 mb-5 bg-white">
              <p className="text-lg font-bold text-[#1A2421]">{dayName} {dateDisplay}</p>
              <p className="text-sm text-[#5C6656] mb-4">{startTime} — {endTime}</p>

              <div className="space-y-4">
                {(confirmed.services || []).map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-9 h-9 rounded-full bg-[#E9EDDF] flex items-center justify-center text-xs font-bold text-[#556B2F]">
                        {(s.staff || '?').substring(0, 2)}
                      </div>
                      {i < (confirmed.services || []).length - 1 && <div className="w-px h-6 bg-[#E5E7E1] mt-1" />}
                    </div>
                    <div>
                      <p className="font-bold text-[#1A2421] text-sm">{s.service}</p>
                      <p className="text-xs text-[#5C6656]">{s.time?.split('-')[0]} · {s.staff}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Add to calendar */}
            <Button
              data-testid="add-to-calendar-btn"
              onClick={() => {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                if (isIOS) {
                  const blob = new Blob([icsContent], { type: 'text/calendar' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'appointment.ics';
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } else {
                  window.open(googleCalUrl, '_blank');
                }
              }}
              className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white py-5 text-base mb-4">
              <CalendarDays size={18} className="mr-2" /> Add to calendar
            </Button>

            {/* Action buttons */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <button onClick={async () => {
                // Cancel old appointments, then go to date/time step with same services
                try {
                  if (confirmed.appointment_ids?.length) {
                    await axios.post(`${API}/public/book/${slug}/cancel`, { appointment_ids: confirmed.appointment_ids });
                  }
                  // Pre-fill cart with same services
                  const svcIds = (confirmed.services || []).map(s => s.service_id).filter(Boolean);
                  if (svcIds.length && biz?.services) {
                    const newCart = svcIds.map(sid => biz.services.find(s => s.id === sid)).filter(Boolean);
                    if (newCart.length) setCart(newCart);
                  }
                  // Pre-fill staff if they had one
                  const staffId = confirmed.services?.[0]?.staff_id;
                  if (staffId && biz?.staff) {
                    const s = biz.staff.find(st => st.id === staffId);
                    if (s) setSelectedStaff(s);
                  }
                  setConfirmed(null);
                  setStep(2); // Go to date & time
                  toast.success('Previous booking cancelled. Pick a new time.');
                } catch { toast.error('Failed to reschedule'); }
              }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-[#E5E7E1] hover:bg-[#F5F7F0] transition-colors" data-testid="reschedule-btn">
                <Clock size={20} className="text-[#5C6656]" />
                <span className="text-xs font-medium text-[#5C6656]">Reschedule</span>
              </button>
              <button disabled={cancellingBooking} onClick={async () => {
                setCancellingBooking(true);
                try {
                  if (confirmed.appointment_ids?.length) {
                    await axios.post(`${API}/public/book/${slug}/cancel`, { appointment_ids: confirmed.appointment_ids });
                  }
                  setCancelledBooking(true);
                  toast.success('Appointment cancelled');
                } catch { toast.error('Failed to cancel'); }
                setCancellingBooking(false);
              }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-[#E5E7E1] hover:bg-[#FEF2F0] transition-colors" data-testid="cancel-booking-btn">
                <X size={20} className="text-[#E2725B]" />
                <span className="text-xs font-medium text-[#E2725B]">{cancellingBooking ? 'Cancelling...' : 'Cancel'}</span>
              </button>
              <button onClick={() => { setStep(0); setConfirmed(null); setCart([]); setSelectedStaff(null); setSelectedTime(''); setForm({name:'',email:'',phone:'',notes:''}); setHoldId(null); setCancelledBooking(false); }}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-[#E5E7E1] hover:bg-[#F5F7F0] transition-colors" data-testid="book-again-btn">
                <ArrowLeft size={20} className="text-[#5C6656]" style={{ transform: 'scaleX(-1)' }} />
                <span className="text-xs font-medium text-[#5C6656]">Book again</span>
              </button>
            </div>

            {/* Cash preference — elegant nudge for cash-preferring salons (Angeline pilot).
                Rendered from the business settings so each tenant can opt-in independently. */}
            {biz?.cash_preferred && !cancelledBooking && (
              <div className="mb-5 rounded-xl overflow-hidden border-2" style={{ borderColor: '#C9A961', background: 'linear-gradient(180deg, #FBF7EE 0%, #F5EED8 100%)' }} data-testid="cash-preference-card">
                <div className="p-5 text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-2" style={{ background: '#C9A961' }}>
                    <span className="text-xl">♛</span>
                  </div>
                  <h3 className="text-lg font-bold tracking-widest text-[#1A2421]" style={{ fontFamily: 'Georgia, serif' }}>CASH IS KING</h3>
                  <p className="text-xs italic text-[#5C6656] mt-1">We accept cash only</p>
                  <div className="my-3 flex items-center justify-center gap-2">
                    <div className="text-4xl font-black" style={{ color: '#C9A961' }}>100%</div>
                    <div className="text-[10px] text-left leading-tight text-[#5C6656] uppercase tracking-wider">
                      of its value<br />retained
                    </div>
                  </div>
                  <p className="text-xs text-[#5C6656] leading-relaxed px-2" style={{ fontFamily: 'Georgia, serif' }}>
                    As a small independent business, card processing fees hurt our margins — so we no longer accept card payments. Please bring cash on the day of your appointment.
                  </p>
                  <p className="text-[10px] text-[#5C6656] mt-2">Thank you for supporting your local independent salon.</p>
                  <p className="text-[10px] uppercase tracking-widest mt-3 font-semibold" style={{ color: '#C9A961' }}>Let's keep cash alive</p>
                </div>
              </div>
            )}

            {/* Cancelled banner */}
            {cancelledBooking && (
              <div className="border border-[#E2725B]/20 bg-[#FEF2F0] rounded-xl p-4 mb-5 text-center">
                <p className="text-sm font-bold text-[#E2725B]">This appointment has been cancelled</p>
                <p className="text-xs text-[#5C6656] mt-1">You'll receive a confirmation email shortly</p>
              </div>
            )}

            {/* Location */}
            {bizAddress && (
              <div className="border border-[#E5E7E1] rounded-xl p-5 bg-white">
                <p className="font-bold text-[#1A2421] mb-2">Location</p>
                <p className="text-sm text-[#5C6656] mb-3">{biz?.name}{bizAddress ? ` · ${bizAddress}` : ''}</p>
                <a href={`https://maps.google.com/?q=${encodeURIComponent(bizAddress)}`} target="_blank" rel="noopener noreferrer"
                  className="block w-full h-32 rounded-lg bg-[#E9EDDF] overflow-hidden relative">
                  <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(bizAddress)}&zoom=15&size=600x200&markers=${encodeURIComponent(bizAddress)}&key=AIzaSyAP-_placeholder`}
                    alt="Map" className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-sm text-[#5C6656]">View on Google Maps</div>'; }} />
                </a>
              </div>
            )}
          </div>
          );
        })()}
        </>)}
      </div>

      <div className="text-center py-4 text-xs text-[#5C6656]">Made by <span className="font-bold text-[#556B2F]">Apptio LD</span></div>
    </div>
  );
}
